import { Component, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { EmissionsFactoryApi } from '@features/emissions/services/emissions-factory-api';
import { DataEndpoint } from '@core/models/general.model';
import { EmissionsFactory, EmissionsFactoryPaginatedModel, EmissionsFactoryRequestModel } from '@features/emissions/models/emissions-factory.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { EmissionsFactorsFilter } from '@features/emissions/components/emissions-factors-filter/emissions-factors-filter';
import { EmissionsFactorsList } from '@features/emissions/components/emissions-factors-list/emissions-factors-list';
import { PaginatorState } from 'primeng/paginator';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-emissions-factors',
  imports: [
    ButtonModule,
    EmissionsFactorsFilter,
    EmissionsFactorsList,
    ToastModule,
    BlockUI,
    ProgressSpinner
  ],
  templateUrl: './emissions-factors.html',
  styleUrl: './emissions-factors.scss',
  providers: [MessageService]
})
export class EmissionsFactors {
  private http = inject(HttpClient);
  private messageService = inject(MessageService);
  private emissionsFactoryApi = inject(EmissionsFactoryApi);
  factorMap: Record<string, string> = {};
  variationMap: Record<string, string> = {};

  //private messageService = inject(MessageService);
  //private emissionsFactoryApi = inject(EmissionsFactoryApi);

  // ✅ Datos
  private allData: EmissionsFactory[] = [];   // TODO lo que llega de API
  filteredData: EmissionsFactory[] = [];      // SOLO lo visible (página actual)

  // ✅ UI state
  isLoading = false;
  sortBy: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;

  data: EmissionsFactory[] = [];
  //filteredData: EmissionsFactory[] = [];
  //isLoading: boolean = false;
  //sortBy: string | null = null;
  //sortOrder: 'asc' | 'desc' | null = null;
  totalRecords: number = 0;
  first: number = 0;

  // Paginación local
  pageNumber = 1;
  pageSize = 10;

  // Filtro local
  searchTerm = '';

  businessId = '';
  officeId = '';

  /*   businessId: string = '';
    officeId: string = '';
    pageNumber: number = 1;
    pageSize: number = 10;
    searchTerm: string = ''; */


  ngOnInit() {
    this.http.get<{ variations: Record<string, string> }>('assets/data/sources.json').subscribe({
      next: (sources) => {
        this.variationMap = sources.variations ?? {};
      },
      error: () => {
        this.variationMap = {};
      }
    });

    // 1) Carga el JSON plano
    this.http.get<Record<string, string>>('assets/data/factors.json').subscribe({
      next: (map) => {
        this.factorMap = map;
        // 2) Trae TODA la data (una vez)
        this.loadAllData();
      },
      error: () => {
        this.factorMap = {};
        this.loadAllData();
      }
    });
  }

  loadAllData() {
    this.isLoading = true;
    this.allData = [];

    const pageSizeForFetch = 500; // sube/baja según volumen
    const baseRequest: EmissionsFactoryRequestModel = {
      businessId: this.businessId,
      officeId: this.officeId || undefined,
      pageNumber: 1,
      pageSize: pageSizeForFetch,
      searchTerm: undefined,
      sortBy: undefined,
      sortOrder: undefined,
    };

    this.fetchPage(baseRequest, 0);
  }

  private fetchPage(request: EmissionsFactoryRequestModel, totalCountSoFar: number) {
    this.emissionsFactoryApi.getEmissionsFactorsByBusiness(request).subscribe({
      next: (response: DataEndpoint<EmissionsFactoryPaginatedModel>) => {
        if (response.Status !== 200) {
          this.isLoading = false;
          return;
        }

        const records = response.Data?.records ?? [];
        const totalCount = response.Data?.pagination?.totalCount ?? 0;

        this.allData.push(...records);

        if (this.allData.length < totalCount && records.length > 0) {
          this.fetchPage({ ...request, pageNumber: (request.pageNumber ?? 1) + 1 }, totalCount);
          return;
        }

        // listo
        this.isLoading = false;

        // reset paginación local
        this.pageNumber = 1;
        this.first = 0;

        // construir vista
        this.applyLocalView();
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Error al cargar datos'
        });
      }
    });
  }

  // ======================================
  // 2) Filtro + Sort + Paginación (LOCAL)
  // ======================================
  private applyLocalView() {
    const filtered = this.applyFilter(this.allData, this.searchTerm);
    const sorted = this.applySort(filtered, this.sortBy, this.sortOrder);

    this.totalRecords = sorted.length;

    const start = this.first ?? 0;
    const end = start + (this.pageSize ?? 10);

    this.filteredData = sorted.slice(start, end);
  }

  private applyFilter(data: EmissionsFactory[], term: string): EmissionsFactory[] {
    const t = (term ?? '').trim().toLowerCase();
    if (!t) return data;

    return data.filter((f) => {
      const code = (f.Code ?? '').toLowerCase();
      const name = (this.factorMap[f.Code] ?? f.Code ?? '').toLowerCase();
      const unit = (f.Unit ?? '').toLowerCase();
      const country = (f.Location?.Country ?? '').toLowerCase();
      const city = (f.Location?.City ?? '').toLowerCase();
      const ref = (f.Reference?.Name ?? '').toLowerCase();

      return (
        code.includes(t) ||
        name.includes(t) ||
        unit.includes(t) ||
        country.includes(t) ||
        city.includes(t) ||
        ref.includes(t)
      );
    });
  }

  private applySort(data: EmissionsFactory[], sortBy: string | null, sortOrder: 'asc' | 'desc' | null) {
    if (!sortBy || !sortOrder) return data;

    const dir = sortOrder === 'asc' ? 1 : -1;

    return [...data].sort((a, b) => {
      const va = this.getSortValue(a, sortBy);
      const vb = this.getSortValue(b, sortBy);

      // numbers
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;

      // booleans
      if (typeof va === 'boolean' && typeof vb === 'boolean') return ((va === vb) ? 0 : (va ? 1 : -1)) * dir;

      const sa = (va ?? '').toString();
      const sb = (vb ?? '').toString();
      return sa.localeCompare(sb, 'es', { sensitivity: 'base' }) * dir;
    });
  }

  private getSortValue(f: EmissionsFactory, key: string): string | number | boolean {
    switch (key) {
      case 'Code': {
        return this.factorMap[f.Code] ?? f.Code ?? '';
      }
      case 'Type': {
        const variation = (f as any).Variation ?? '';
        return this.variationMap[variation] ?? variation;
      }
      case 'CreatedAt':
        return new Date((f as any).CreatedAt).getTime(); // o f.CreatedAt si es Date
      case 'Unit':
        return f.Unit ?? '';
      case 'IsBiomass':
        return !!(f as any).IsBiomass;
      case 'Uncertainty':
        return Number((f as any).Uncertainty ?? 0);

      // Gases
      case 'CO2':
      case 'CH4':
      case 'N2O':
      case 'SF6':
      case 'NF3':
      case 'CO2e':
        return this.getGasNumeric((f as any).Gases ?? [], key);

      // paths anidados
      case 'Location.Country':
        return (f as any).Location?.Country ?? '';
      case 'Location.City':
        return (f as any).Location?.City ?? '';
      case 'Reference.Name':
        return (f as any).Reference?.Name ?? '';
      case 'Date.Year':
        return (f as any).Date?.Year?.toString() ?? '';
      default:
        // fallback simple
        return (f as any)[key] ?? '';
    }
  }

  private getGasNumeric(gases: { Name: string; Value: number }[], gasName: string): number {
    const gas = gases.find(g => g.Name === gasName);
    return gas ? Number(gas.Value) : -Infinity; // para que los '-' queden al final en asc
  }

  loadData() {
    this.isLoading = true;

    const request: EmissionsFactoryRequestModel = {
      businessId: this.businessId,
      officeId: this.officeId || undefined,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm || undefined,
      sortBy: this.sortBy || undefined,
      sortOrder: this.sortOrder || undefined,
    };

    this.emissionsFactoryApi.getEmissionsFactorsByBusiness(request).subscribe({
      next: (response: DataEndpoint<EmissionsFactoryPaginatedModel>) => {
        if (response.Status === 200) {
          this.data = response.Data?.records ?? [];
          this.filteredData = response.Data?.records ?? [];
          this.totalRecords = response.Data?.pagination?.totalCount ?? 0;
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message
        });
      }
    })
  }

  handlePageChange(event: PaginatorState) {
    this.pageNumber = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? 10;
    this.first = event.first ?? 0;
    this.applyLocalView();
    //this.loadData();
  }

  handleToggleSort(columnName: string) {
    if (this.sortBy === columnName) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = columnName;
      this.sortOrder = 'asc';
    }
    this.applyLocalView();
    //this.loadData();
  }

  handleFilter(search: string) {
    this.searchTerm = search;
    this.pageNumber = 1;
    this.first = 0;
    this.applyLocalView();
    //this.loadData();
  }

  handleClearFilters() {
    this.searchTerm = '';
    this.pageNumber = 1;
    this.first = 0;
    this.applyLocalView();
    //this.loadData();
  }

}
